import type {
  Task,
  TaskComment,
  TaskDependency,
  TaskStatus,
} from "@/api/tasks";
import type { WsMessage } from "@/api/websocket";
import { publishTaskComment } from "@/lib/task-comment-bus";
import { resolveRefine } from "@/lib/task-refine-bus";
import { useDirectSessionProgressStore } from "@/stores/direct-session-progress-store";
import { announceTaskNeedsAttention } from "./apply-agent-heartbeat-event";
import { rebindReplacedTaskFrames } from "./rebind-replaced-task-frames";

import type { useTasksStore } from "@/stores/tasks-store";
import type { FrameApplyContext } from "./companion-frame-reducer-shared";

/**
 * A task event's change to the tasks store, and nothing else: the
 * Workspace's reducer adds its side effects around it, a desktop pin window
 * applies it to the tasks it loaded (`desktop-pin-project-data.ts`).
 */
export function applyTaskStoreEvent(
  msg: WsMessage,
  tasksStore: typeof useTasksStore,
): void {
  const tasks = tasksStore.getState();
  const data = msg.data;
  switch (msg.type) {
    case "task.created":
      tasks.handleTaskCreated(data as unknown as Task);
      break;
    case "task.updated":
      tasks.handleTaskUpdated(
        data as unknown as Task & { replaces_task_id?: string },
      );
      break;
    case "task.status_changed":
      tasks.updateTaskStatus(data.task_id as string, data.status as TaskStatus);
      break;
    case "task.blocked":
      tasks.handleTaskBlocked(
        data.task_id as string,
        data.blocked_by as TaskDependency[],
      );
      break;
    case "task.unblocked":
      tasks.handleTaskUnblocked(data.task_id as string);
      break;
  }
}

export function applyTaskEvent(msg: WsMessage, ctx: FrameApplyContext): void {
  switch (msg.type) {
    case "task.refined": {
      const { request_id, title, description, ok } = msg.data as {
        request_id?: string;
        title?: string;
        description?: string;
        ok?: boolean;
      };
      if (request_id)
        resolveRefine(request_id, {
          title: title ?? "",
          description: description ?? "",
          ok: Boolean(ok),
        });
      break;
    }
    case "task.status_changed":
    case "task.created":
    case "task.blocked":
    case "task.unblocked":
      applyTaskStoreEvent(msg, ctx.tasksStore);
      break;
    case "task.updated": {
      const task = msg.data as unknown as Task & { replaces_task_id?: string };
      // Read the stored row BEFORE the update overwrites it — the budget
      // announcement is for the crossing, not for the flagged state.
      const previous = ctx.tasksStore
        .getState()
        .tasks.find((item) => item.id === task.id);
      applyTaskStoreEvent(msg, ctx.tasksStore);
      ctx.notificationStore.getState().noteTaskUpdated(task.id);
      rebindReplacedTaskFrames(task);
      if (task.replaces_task_id && task.replaces_task_id !== task.id) {
        ctx.notificationStore
          .getState()
          .rebindTask(task.replaces_task_id, task.id);
      }
      announceTaskNeedsAttention(previous, task, ctx);
      // A settled direct chat has no live line, even if its clear was missed.
      const directStatus = task.direct_session?.status;
      if (
        directStatus &&
        directStatus !== "running" &&
        directStatus !== "waiting_approval"
      ) {
        useDirectSessionProgressStore
          .getState()
          .apply({ task_id: task.id, text: "" });
      }
      break;
    }
    case "task.comment.created": {
      const d = msg.data as { task_id: string; comment: TaskComment };
      if (d.task_id && d.comment)
        publishTaskComment({ task_id: d.task_id, comment: d.comment });
      break;
    }
  }
}

export function applyRuntimeEvent(
  msg: WsMessage,
  ctx: FrameApplyContext,
): void {
  switch (msg.type) {
    case "runtime.tokens": {
      const {
        agent_key,
        space_id,
        total_input_tokens,
        total_output_tokens,
        last_input_tokens,
        last_output_tokens,
        last_reasoning_tokens,
        context_used_tokens,
        context_window_max,
        runtime_session_id,
        conversation_id,
      } = msg.data as {
        agent_key?: string;
        space_id?: string;
        total_input_tokens?: number;
        total_output_tokens?: number;
        last_input_tokens?: number;
        last_output_tokens?: number;
        last_reasoning_tokens?: number;
        context_used_tokens?: number | null;
        context_window_max?: number | null;
        runtime_session_id?: string;
        conversation_id?: string;
      };
      const totalInput = Number(total_input_tokens ?? 0);
      const totalOutput = Number(total_output_tokens ?? 0);
      const lastInput =
        last_input_tokens === undefined ? undefined : Number(last_input_tokens);
      const lastOutput =
        last_output_tokens === undefined
          ? undefined
          : Number(last_output_tokens);
      const lastReasoning =
        last_reasoning_tokens === undefined
          ? undefined
          : Number(last_reasoning_tokens);
      const spaceIdForTokens =
        space_id ?? ctx.projectStore.getState().currentSpace?.id ?? "";
      if (agent_key && spaceIdForTokens) {
        const stored =
          ctx.companionStore.getState().sessionTokens[
            `${agent_key.toLowerCase()}:${spaceIdForTokens}`
          ];
        const active =
          ctx.companionStore.getState().agentSessions[
            `${agent_key.toLowerCase()}:${spaceIdForTokens}`
          ];
        const maintenanceInvalidation =
          context_used_tokens === null && last_input_tokens === undefined;
        if (
          maintenanceInvalidation &&
          runtime_session_id &&
          ((active?.runtimeSessionId &&
            active.runtimeSessionId !== runtime_session_id) ||
            (stored?.runtimeSessionId &&
              stored.runtimeSessionId !== runtime_session_id))
        )
          return;
        const previous =
          runtime_session_id &&
          stored?.runtimeSessionId === runtime_session_id &&
          (!conversation_id ||
            !stored.conversationId ||
            stored.conversationId === conversation_id)
            ? stored
            : undefined;
        ctx.companionStore
          .getState()
          .setSessionTokens(agent_key, spaceIdForTokens, {
            input:
              total_input_tokens === undefined
                ? (previous?.input ?? 0)
                : totalInput,
            output:
              total_output_tokens === undefined
                ? (previous?.output ?? 0)
                : totalOutput,
            lastInput,
            lastOutput,
            lastReasoning,
            contextUsed: context_used_tokens,
            max:
              context_window_max === undefined
                ? (previous?.max ?? null)
                : context_window_max,
            runtimeSessionId: runtime_session_id,
            conversationId: conversation_id,
          });
      }
      break;
    }
  }
}
